import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/helios-logo-real.png';
import { validateData, ChatDomainSchema, ChatEmailSchema, ChatVerificationSchema, ChatMessageSchema } from '../utils/validationSchemas';

const Chat = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState('domain'); // domain, email, verify, chat
  const [formData, setFormData] = useState({
    domain: '',
    email: '',
    code: ''
  });
  const [sessionToken, setSessionToken] = useState('');
  const [challengeId, setChallengeId] = useState('');
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(scrollToBottom, [messages]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSendCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate domain
    const domainValidation = validateData(ChatDomainSchema, { domain: formData.domain });
    if (!domainValidation.isValid) {
      setError(domainValidation.error);
      setIsLoading(false);
      return;
    }

    // Validate email
    const emailValidation = validateData(ChatEmailSchema, { email: formData.email });
    if (!emailValidation.isValid) {
      setError(emailValidation.error);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/verification/send-code', {
        email: formData.email,
        domain: formData.domain
      });

      setMaskedPhone(response.data.phoneNumber);
      setChallengeId(response.data.challengeId);
      setStep('verify');
    } catch (error) {
      // Use generic error message for security
      setError('Failed to send verification code. Please check your information and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate verification code
    const validation = validateData(ChatVerificationSchema, { code: formData.code });
    if (!validation.isValid) {
      setError(validation.error);
      setIsLoading(false);
      return;
    }

    try {
      const response = await axios.post('/api/verification/verify-code', {
        challengeId: challengeId,
        code: validation.data.code
      });

      setSessionToken(response.data.sessionToken);
      setMessages([
        {
          type: 'system',
          text: `Welcome ${response.data.user.displayName}! I'm your IT helpdesk assistant. How can I help you today?`
        }
      ]);
      setStep('chat');
    } catch (error) {
      // Use generic error message for security
      setError('Invalid verification code. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim()) return;

    // Validate message
    const validation = validateData(ChatMessageSchema, { message: currentMessage });
    if (!validation.isValid) {
      setError(validation.error);
      return;
    }

    const userMessage = currentMessage;
    setCurrentMessage('');

    setMessages(prev => [...prev, { type: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      const response = await axios.post('/api/chat/message', {
        message: validation.data.message
      }, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      });

      const assistantMessage = {
        type: 'assistant',
        text: response.data.response.text,
        actions: response.data.response.actions
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      let errorMessage = 'Sorry, I encountered an error. Please try again.';

      // Handle authentication errors
      if (error.response?.status === 401) {
        const errorCode = error.response?.data?.code;
        if (errorCode === 'SESSION_EXPIRED' || errorCode === 'INVALID_TOKEN' || errorCode === 'AUTH_FAILED' ||
            errorCode === 'MISSING_TOKEN' || errorCode === 'INVALID_USER') {
          errorMessage = `${error.response?.data?.error || 'Your session has expired.'}

Please refresh the page and verify your identity again to continue using the system.`;
        }
      }

      setMessages(prev => [...prev, {
        type: 'system',
        text: errorMessage
      }]);
    } finally {
      setIsLoading(false);
      // Focus back on input field after message is sent
      inputRef.current?.focus();
    }
  };

  const handleAction = async (actionType) => {
    setIsLoading(true);
    
    try {
      let endpoint = '';
      let method = 'POST';
      
      if (actionType === 'password-reset') {
        endpoint = '/api/services/password-reset';
      } else if (actionType === 'account-unlock') {
        endpoint = '/api/services/account-unlock';
      } else if (actionType === 'user-info') {
        endpoint = '/api/services/user-info';
        method = 'GET';
      }

      const response = method === 'GET' 
        ? await axios.get(endpoint, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          })
        : await axios.post(endpoint, {}, {
            headers: {
              'Authorization': `Bearer ${sessionToken}`
            }
          });

      // Handle different response types
      if (actionType === 'user-info') {
        const userInfo = response.data.userInfo;
        const infoText = `Here's your account information:

📧 Email: ${userInfo.email}
👤 Name: ${userInfo.name}
📞 Phone: ${userInfo.phoneNumber}
🏢 Organization: ${userInfo.organization}
🌐 Domain: ${userInfo.domain}
✅ Status: ${userInfo.accountStatus}
🕒 Last Verified: ${userInfo.lastVerified}`;

        setMessages(prev => [...prev, { 
          type: 'system', 
          text: infoText
        }]);
      } else {
        setMessages(prev => [...prev, { 
          type: 'system', 
          text: response.data.message,
          details: response.data.tempPassword ? `Temporary password: ${response.data.tempPassword}` : null
        }]);
      }
    } catch (error) {
      let errorMessage = error.response?.data?.error || 'Action failed. Please try again.';
      
      // Handle authentication errors with helpful guidance
      if (error.response?.status === 401) {
        const errorCode = error.response?.data?.code;
        if (errorCode === 'SESSION_EXPIRED' || errorCode === 'INVALID_TOKEN' || errorCode === 'AUTH_FAILED' || 
            errorCode === 'MISSING_TOKEN' || errorCode === 'INVALID_USER') {
          errorMessage = `${errorMessage} 

Please refresh the page and verify your identity again to continue using the system.`;
        }
      }
      
      setMessages(prev => [...prev, { 
        type: 'system', 
        text: errorMessage
      }]);
    } finally {
      setIsLoading(false);
    }
  };



  const renderDomainStep = () => (
    <div className="h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-32 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative z-10 bg-slate-900 bg-opacity-40 backdrop-blur-md max-w-md w-full shadow-2xl rounded-lg p-8 border border-blue-500 border-opacity-10">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Helios Logo" className="h-16 w-auto" />
        </div>
        <h2 className="text-3xl font-bold mb-2 text-white text-center">IT Helpdesk Assistant</h2>
        <p className="text-slate-400 mb-8 text-center">Enter your organization domain to get started</p>
        <form onSubmit={(e) => { e.preventDefault(); setStep('email'); }}>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-blue-400 mb-2">Company Domain</label>
            <input
              type="text"
              name="domain"
              value={formData.domain}
              onChange={handleInputChange}
              placeholder="company.com"
              className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
              required
            />
          </div>
          {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}
          <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={!formData.domain}>
            Continue
          </button>
        </form>
      </div>
    </div>
  );

  const renderEmailStep = () => (
    <div className="h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-32 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative z-10 bg-slate-900 bg-opacity-40 backdrop-blur-md max-w-md w-full shadow-2xl rounded-lg p-8 border border-blue-500 border-opacity-10">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Helios Logo" className="h-16 w-auto" />
        </div>
        <h2 className="text-3xl font-bold mb-2 text-white text-center">Verify Your Identity</h2>
        <p className="text-slate-400 mb-8 text-center">Enter your work email to receive a verification code</p>
        <form onSubmit={handleSendCode}>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-blue-400 mb-2">Work Email</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={`user@${formData.domain}`}
              className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm"
              required
            />
          </div>
          {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}
          <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading || !formData.email}>
            {isLoading ? 'Sending...' : 'Send Verification Code'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderVerifyStep = () => (
    <div className="h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 flex items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-32 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      <div className="relative z-10 bg-slate-900 bg-opacity-40 backdrop-blur-md max-w-md w-full shadow-2xl rounded-lg p-8 border border-blue-500 border-opacity-10">
        <div className="flex justify-center mb-8">
          <img src={logo} alt="Helios Logo" className="h-16 w-auto" />
        </div>
        <h2 className="text-3xl font-bold mb-2 text-white text-center">Verify Your Code</h2>
        <p className="text-slate-400 mb-8 text-center">We've sent a 6-digit code to <span className="text-blue-400 font-semibold">{maskedPhone}</span></p>
        <form onSubmit={handleVerifyCode}>
          <div className="mb-6">
            <label className="block text-sm font-semibold text-blue-400 mb-2">Verification Code</label>
            <input
              type="text"
              name="code"
              value={formData.code}
              onChange={handleInputChange}
              placeholder="123456"
              maxLength="6"
              className="w-full px-4 py-2 rounded-lg bg-slate-800 bg-opacity-50 border border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400 transition-all backdrop-blur-sm text-center text-2xl tracking-widest font-bold"
              required
            />
          </div>
          {error && <div className="text-red-400 text-sm mb-4 bg-red-500 bg-opacity-10 p-3 rounded border border-red-500 border-opacity-20">{error}</div>}
          <button type="submit" className="w-full bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white font-semibold py-2 px-4 rounded-lg transition-all hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading || !formData.code}>
            {isLoading ? 'Verifying...' : 'Verify'}
          </button>
        </form>
      </div>
    </div>
  );

  const renderChatStep = () => (
    <div className="h-screen bg-gradient-to-br from-blue-950 via-slate-950 to-slate-900 flex flex-col relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 right-32 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{animationDelay: '2s'}}></div>
        <div className="absolute top-1/2 left-1/3 w-96 h-96 bg-cyan-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse" style={{animationDelay: '4s'}}></div>
      </div>

      {/* Header */}
      <div className="relative z-10 bg-gradient-to-r from-slate-900 to-slate-800 border-b border-blue-500 border-opacity-20 px-6 py-4 flex items-center justify-between shadow-lg backdrop-blur-sm">
        <button
          onClick={() => navigate('/')}
          className="text-blue-400 hover:text-blue-300 font-semibold transition-colors flex items-center gap-2"
        >
          <span>←</span> Back
        </button>
        <div className="flex items-center gap-3">
          <img src={logo} alt="Helios Logo" className="h-8 w-auto" />
          <h2 className="text-xl font-bold text-white">IT Helpdesk Assistant</h2>
        </div>
        <div className="w-20"></div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex overflow-hidden relative z-10">
        {/* Sidebar Menu */}
        <div className="w-64 bg-slate-900 bg-opacity-40 backdrop-blur-md border-r border-blue-500 border-opacity-10 overflow-y-auto p-4 space-y-3">
          <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">Quick Actions</h3>

          <button
            onClick={() => handleAction('password-reset')}
            disabled={isLoading}
            className="w-full text-left px-4 py-3 rounded-lg bg-blue-500 bg-opacity-10 hover:bg-blue-500 hover:bg-opacity-20 border border-blue-500 border-opacity-20 hover:border-blue-400 text-slate-100 hover:text-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
          >
            <div className="font-semibold text-sm">🔐 Reset Password</div>
            <div className="text-xs text-slate-400 mt-1">Get a temporary password</div>
          </button>

          <button
            onClick={() => handleAction('account-unlock')}
            disabled={isLoading}
            className="w-full text-left px-4 py-3 rounded-lg bg-blue-500 bg-opacity-10 hover:bg-blue-500 hover:bg-opacity-20 border border-blue-500 border-opacity-20 hover:border-blue-400 text-slate-100 hover:text-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
          >
            <div className="font-semibold text-sm">🔓 Unlock Account</div>
            <div className="text-xs text-slate-400 mt-1">Regain access to your account</div>
          </button>

          <button
            onClick={() => handleAction('user-info')}
            disabled={isLoading}
            className="w-full text-left px-4 py-3 rounded-lg bg-blue-500 bg-opacity-10 hover:bg-blue-500 hover:bg-opacity-20 border border-blue-500 border-opacity-20 hover:border-blue-400 text-slate-100 hover:text-blue-300 transition-all disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
          >
            <div className="font-semibold text-sm">👤 View Account Info</div>
            <div className="text-xs text-slate-400 mt-1">Check your profile details</div>
          </button>

          <div className="border-t border-blue-500 border-opacity-10 pt-4 mt-4">
            <h3 className="text-sm font-bold text-blue-400 uppercase tracking-wider mb-4">Help Topics</h3>

            <div className="space-y-2 text-sm">
              <div className="px-4 py-2 rounded bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-10 text-slate-300 backdrop-blur-sm">
                <div className="font-semibold">❓ Common Issues</div>
              </div>
              <div className="px-4 py-2 rounded bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-10 text-slate-300 backdrop-blur-sm">
                <div className="font-semibold">📧 Email & Calendar</div>
              </div>
              <div className="px-4 py-2 rounded bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-10 text-slate-300 backdrop-blur-sm">
                <div className="font-semibold">💻 Software & Tools</div>
              </div>
              <div className="px-4 py-2 rounded bg-blue-500 bg-opacity-10 border border-blue-500 border-opacity-10 text-slate-300 backdrop-blur-sm">
                <div className="font-semibold">🔒 Security & Access</div>
              </div>
            </div>
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          {/* Messages Container */}
          <div className="flex-1 overflow-y-auto px-6 py-6 space-y-4 bg-gradient-to-b from-transparent to-slate-900 to-opacity-20">
            {messages.length === 0 && (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-5xl mb-4">👋</div>
                  <h3 className="text-xl font-bold text-blue-300 mb-2">Welcome to IT Support</h3>
                  <p className="text-slate-400 mb-6">Select an action from the menu or describe your issue</p>
                  <div className="bg-slate-900 bg-opacity-40 backdrop-blur-md rounded-lg p-4 text-left text-sm text-slate-300 max-w-sm border border-blue-500 border-opacity-10">
                    <p className="font-semibold text-blue-400 mb-2">How can we help?</p>
                    <ul className="space-y-1 text-xs">
                      <li>• Need to reset your password?</li>
                      <li>• Account locked or access issues?</li>
                      <li>• Questions about your account?</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-sm px-4 py-3 rounded-lg shadow-md backdrop-blur-sm ${
                  message.type === 'user'
                    ? 'bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-br-none'
                    : message.type === 'system'
                    ? 'bg-yellow-500 bg-opacity-10 text-yellow-100 border border-yellow-500 border-opacity-30 rounded-bl-none'
                    : 'bg-slate-800 bg-opacity-40 text-slate-100 border border-blue-500 border-opacity-20 rounded-bl-none'
                }`}>
                  <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.text}</p>
                  {message.details && (
                    <div className="mt-2 pt-2 border-t border-current border-opacity-20">
                      <code className="text-xs bg-slate-900 bg-opacity-50 px-2 py-1 rounded block overflow-x-auto">
                        {message.details}
                      </code>
                    </div>
                  )}
                  {message.actions && (
                    <div className="mt-3 space-y-2">
                      {message.actions.map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={() => handleAction(action.type)}
                          disabled={isLoading}
                          className="w-full bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-semibold py-2 px-3 rounded transition-all hover:shadow-lg"
                        >
                          {action.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-slate-800 bg-opacity-40 text-slate-100 border border-blue-500 border-opacity-20 px-4 py-3 rounded-lg rounded-bl-none shadow-md backdrop-blur-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce"></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Form */}
          <form onSubmit={handleSendMessage} className="bg-gradient-to-r from-slate-900 to-slate-800 border-t border-blue-500 border-opacity-20 px-6 py-4 flex gap-3 shadow-lg backdrop-blur-md">
            <input
              ref={inputRef}
              type="text"
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              placeholder="Describe your issue..."
              disabled={isLoading}
              className="input-field flex-1 bg-slate-800 bg-opacity-50 border-blue-500 border-opacity-20 text-slate-100 placeholder-slate-500 backdrop-blur-sm"
            />
            <button
              type="submit"
              disabled={isLoading || !currentMessage.trim()}
              className="btn-primary px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-lg"
            >
              Send
            </button>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="chat-wrapper">
      {step === 'domain' && renderDomainStep()}
      {step === 'email' && renderEmailStep()}
      {step === 'verify' && renderVerifyStep()}
      {step === 'chat' && renderChatStep()}
    </div>
  );
};

export default Chat;